import is from '@sindresorhus/is';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { difference } from 'lodash';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';
import type { LanguageCode } from '~/shared/internationalization';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type JobItemDetails = JobLevelDetails | JobRoleDetails;

export const patchMissingTranslations = (
    item: JobItemDetails,
    availableLanguages: readonly LanguageCode[],
): JobItemDetails => {
    const missingLanguages = difference(
        availableLanguages,
        item.translations.map((translation) => translation.language),
    );
    const isGenerating = item.profileGenerationInfo?.inProgress ?? false;

    const patchedJobProfile = is.undefined(item.jobProfile)
        ? undefined
        : {
              ...item.jobProfile,
              translations: [
                  ...item.jobProfile.translations,
                  ...missingLanguages.filter((_) => isGenerating).map((language) => ({ language, title: '' })),
              ],
          };

    return {
        ...item,
        jobProfile: patchedJobProfile,
        translations: [
            ...item.translations,
            ...missingLanguages.filter((_) => isGenerating).map((language) => ({ language, title: '' })),
        ],
    };
};

export const useJobItemDetails = ({
    architectureId,
    enabled = true,
    itemId,
    itemType,
    version,
}: {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly version: string | undefined;
}): UseQueryResult<JobItemDetails, Error> => {
    const { readJobLevelDetails, readJobRoleDetails } = useApplicationFetchers();

    const {
        applicationState: { customization },
    } = useApplicationContext();
    const availableLanguages = customization.get('commonConfiguration').get('languages').unwrapOr([]);

    const getQueryKey = (): QueryKey => {
        if (itemType === 'job-role') {
            return jobArchitectureKeys.role(architectureId, itemId, version);
        }
        if (itemType === 'job-level') {
            return jobArchitectureKeys.level(architectureId, itemId, version);
        }

        throw new Error(`Invalid item type: ${itemType}`);
    };

    return useQuery<JobItemDetails, Error>({
        enabled: enabled === true,
        placeholderData: (previousData) => {
            return previousData?.id === itemId ? previousData : undefined;
        },
        queryFn: async ({ signal }) => {
            const result =
                itemType === 'job-role'
                    ? await readJobRoleDetails({
                          jobArchitectureId: architectureId,
                          jobRoleId: itemId,
                          signal,
                          version,
                      })
                    : await readJobLevelDetails({
                          jobArchitectureId: architectureId,
                          jobLevelId: itemId,
                          signal,
                          version,
                      });

            if (result.isErr === true) {
                throw result.error;
            }

            return patchMissingTranslations(result.value, availableLanguages);
        },
        queryKey: getQueryKey(),
        refetchInterval: (query) => ((query.state.data?.profileGenerationInfo?.inProgress ?? false) ? 1000 : undefined),
        staleTime: 1000,
        throwOnError: false,
    });
};
